<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount, useSlots } from 'vue';
import { useFocusTrap, useScrollLock } from './useOverlayA11y';
import LumiIcon from '../icons/LumiIcon.vue';
import { reserveFormActionBar, releaseFormActionBar } from './formActionBar';

const props = withDefaults(
  defineProps<{
    open?: boolean;
    title?: string;
    subtitle?: string;
    size?: 'md' | 'lg' | 'xl';
    closeLabel?: string;
    /** When false, hides the close button and ignores backdrop/Escape dismissal. */
    dismissible?: boolean;
    dismissOnBackdrop?: boolean;
    /**
     * 'confirm' outranks a plain 'modal' in the overlay stacking ladder — used by
     * NoticeDialog so a confirmation raised from inside another modal always wins
     * the top layer, regardless of teleport/DOM mount order. See tokens/layers.css.
     */
    layer?: 'modal' | 'confirm';
    /**
     * On a phone (the shell's mobile width, below 768 px) the dialog docks to the
     * bottom edge as a sheet: full width, within thumb reach, clear of the home
     * indicator. A wider screen keeps the centred dialog. For a quick pick made on
     * the go (the home clock's project choice), not for a form.
     */
    sheet?: boolean;
  }>(),
  {
    open: false,
    size: 'md',
    closeLabel: 'Schließen',
    dismissible: true,
    dismissOnBackdrop: true,
    layer: 'modal',
    sheet: false,
  },
);

const emit = defineEmits<{ close: [] }>();

// The template's root is `<Teleport>`, which renders no DOM node of its own —
// Vue's automatic fallthrough-attrs-to-root-element does not reach through
// it, so a consumer's `data-test`/`class`/etc. on `<Modal>` would otherwise
// be silently dropped instead of landing on the backdrop (the suite-role
// confirmation, users & assignments rework M2, needed `data-test`
// to actually resolve in a real browser).
defineOptions({ inheritAttrs: false });

const openRef = computed(() => props.open);
const dialogRef = ref<HTMLElement | null>(null);
useScrollLock(openRef);
const { isTopmost } = useFocusTrap(openRef, dialogRef);

const slots = useSlots();
// Reserve the bottom-right corner for LumiShell's floating launcher whenever
// this modal has a footer to protect (outcome invariant 8) — see
// formActionBar.ts for why the layering z-index ladder alone doesn't cover
// this. A footer-less modal (no Save/Cancel to protect) never reserves.
let avoidanceToken: symbol | null = null;
watch(
  openRef,
  (isOpen) => {
    if (isOpen && slots.footer) {
      avoidanceToken = reserveFormActionBar();
    } else {
      releaseFormActionBar(avoidanceToken);
      avoidanceToken = null;
    }
  },
  { immediate: true },
);
onBeforeUnmount(() => releaseFormActionBar(avoidanceToken));

function onBackdrop(e: MouseEvent) {
  if (e.target === e.currentTarget && props.dismissible && props.dismissOnBackdrop) emit('close');
}

// Self-contained Escape-to-close so the primitive needs no app-level helper
// (some consumers have no global escape handler). Scroll lock and focus
// trap/return are shared via useOverlayA11y (nested-safe across primitives).
// `isTopmost()` keeps Escape scoped to whichever overlay is actually on top —
// without it, a confirm raised from inside this modal would close both at once.
// `defaultPrevented` is the contract an inner control uses to say "this Escape
// was mine" (a Combobox closing its list, say) — the same one app-kit's
// `installEscapeClose` honours; without it one keypress closed the list AND
// the modal around it (#943).
function onKey(e: KeyboardEvent) {
  if (e.key !== 'Escape' || e.defaultPrevented) return;
  if (props.open && props.dismissible && isTopmost()) emit('close');
}

watch(
  () => props.open,
  (open) => {
    if (typeof document === 'undefined') return;
    if (open) document.addEventListener('keydown', onKey);
    else document.removeEventListener('keydown', onKey);
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (typeof document === 'undefined') return;
  document.removeEventListener('keydown', onKey);
});
</script>

<template>
  <Teleport to="body">
    <div v-if="open" v-bind="$attrs" class="lumi-modal-backdrop" :class="{ 'lumi-modal-backdrop--confirm': layer === 'confirm', 'lumi-modal-backdrop--sheet': sheet }" @click="onBackdrop">
      <div
        ref="dialogRef"
        class="lumi-modal"
        :class="{ 'lumi-modal--lg': size === 'lg', 'lumi-modal--xl': size === 'xl', 'lumi-modal--sheet': sheet }"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
      >
        <div class="lumi-modal__head">
          <div class="lumi-modal__heading">
            <h3 class="lumi-modal__title">{{ title }}</h3>
            <p v-if="subtitle" class="lumi-modal__subtitle">{{ subtitle }}</p>
          </div>
          <button
            v-if="dismissible"
            type="button"
            class="lumi-modal__close"
            data-modal-close
            :aria-label="closeLabel"
            @click="emit('close')"
          >
            <LumiIcon name="remove" width="18" height="18" stroke-width="2" aria-hidden="true" />
          </button>
        </div>
        <div class="lumi-modal__body"><slot /></div>
        <div v-if="$slots.footer" class="lumi-modal__foot"><slot name="footer" /></div>
      </div>
    </div>
  </Teleport>
</template>

<style>
/* Overlay stacking ladder — tokens/layers.css (workbench < drawer < modal <
   confirm < toast): a modal must win over both a workbench and a drawer it
   was opened from on top of; `layer="confirm"` (NoticeDialog) must in turn
   win over a plain modal, because a confirmation is often standing between
   the user and an irreversible act. See Workbench.vue for why this can't be
   left to teleport/DOM order. Keep in step with Workbench.vue, Drawer.vue and
   NoticeToastHost.vue — never hand-write a z-index here (check-raw-layer-usage).

   The dialog style (spec §8): white surface on the panel edge, 16 px radius,
   the floating shadow; header and footer part from the body by a fade, never
   a hairline; the scrim is espresso at 40 % with a 2 px blur (--overlay). */
.lumi-modal-backdrop{position:fixed;inset:0;background:var(--overlay);backdrop-filter:blur(2px);display:flex;align-items:center;justify-content:center;padding:var(--space-6);z-index:var(--layer-overlay-modal);}
.lumi-modal-backdrop--confirm{z-index:var(--layer-overlay-confirm);}
.lumi-modal{background:var(--surface-card);border:1px solid var(--panel-edge);border-radius:var(--radius-xl);box-shadow:var(--shadow-modal);width:100%;max-width:480px;font-family:var(--font-body);display:flex;flex-direction:column;max-height:85vh;overflow:clip;}
.lumi-modal--lg{max-width:680px;}
.lumi-modal--xl{max-width:1100px;}
.lumi-modal__head{position:relative;z-index:var(--layer-raised);display:flex;align-items:center;justify-content:space-between;gap:var(--space-4);padding:var(--space-5) var(--space-6) var(--space-3);flex-shrink:0;}
.lumi-modal__head::after{content:'';position:absolute;left:0;right:0;top:100%;height:var(--fade-h);background:linear-gradient(to bottom,var(--surface-card),transparent);pointer-events:none;}
.lumi-modal__heading{min-width:0;}
.lumi-modal__title{font-size:var(--text-base);font-weight:var(--weight-bold);letter-spacing:var(--tracking-display);color:var(--text-heading);margin:0;}
.lumi-modal__subtitle{font-size:var(--text-sm);color:var(--text-secondary);margin:2px 0 0;}
.lumi-modal__close{border:none;background:none;cursor:pointer;color:var(--text-secondary);width:32px;height:32px;display:flex;align-items:center;justify-content:center;border-radius:var(--radius-sm);flex-shrink:0;}
.lumi-modal__close:hover{color:var(--text-heading);background:var(--surface-wash);}
.lumi-modal__body{padding:var(--space-3) var(--space-6);overflow-y:auto;font-size:var(--text-sm);color:var(--text-body);}
.lumi-modal__foot{position:relative;z-index:var(--layer-raised);display:flex;justify-content:flex-end;gap:var(--space-3);padding:var(--space-3) var(--space-6) var(--space-5);flex-shrink:0;}
.lumi-modal__foot::before{content:'';position:absolute;left:0;right:0;bottom:100%;height:var(--fade-h);background:linear-gradient(to top,var(--surface-card),transparent);pointer-events:none;}
/* The sheet (`sheet`): on a phone the dialog rises from the bottom edge, full
   width, top corners rounded, its foot kept clear of the home indicator; the
   handle bar says it is a sheet. Same 767 px line as the shell's mobile layout. */
@media (max-width:767px){
.lumi-modal-backdrop--sheet{align-items:flex-end;padding:0;}
.lumi-modal--sheet{max-width:none;max-height:85vh;max-height:85dvh;border-bottom:0;border-radius:var(--radius-xl) var(--radius-xl) 0 0;padding-bottom:env(safe-area-inset-bottom);animation:lumi-modal-sheet-in var(--dur-base) var(--ease-out);}
.lumi-modal--sheet::before{content:'';display:block;flex-shrink:0;width:36px;height:4px;margin:var(--space-2) auto 0;border-radius:var(--radius-pill);background:color-mix(in srgb,var(--text-tertiary) 45%,transparent);}
.lumi-modal--sheet .lumi-modal__head{padding:var(--space-3) var(--space-5) var(--space-2);}
.lumi-modal--sheet .lumi-modal__body{padding:var(--space-2) var(--space-3) var(--space-4);}
.lumi-modal--sheet .lumi-modal__close{width:44px;height:44px;}
}
@keyframes lumi-modal-sheet-in{from{transform:translateY(100%);}to{transform:none;}}
@media (prefers-reduced-motion:reduce){.lumi-modal--sheet{animation:none;}}
</style>
